import type { ReactNode } from 'react';

import css from './estructura.module.css';

/**
 * La forma de todas las pantallas: cabecera fija, contenido con scroll y
 * barra de acción fija abajo.
 *
 * Está en un solo sitio porque el área segura del iPhone es fácil de olvidar y
 * cuando se olvida el botón de "Finalizar" queda debajo de la barra del
 * sistema: el usuario ve el botón y no puede pulsarlo.
 */
export function Pantalla({
    children,
    fondo,
    sentido = 'adelante',
}: {
    children?: ReactNode;
    fondo?: 'oscuro';
    sentido?: 'adelante' | 'atras';
}) {
    return (
        <div className={css.pantalla} data-fondo={fondo} data-sentido={sentido}>
            {children}
        </div>
    );
}

export function Cabecera({
    titulo,
    subtitulo,
    alVolver,
    derecha,
}: {
    titulo: string;
    subtitulo?: string | undefined;
    alVolver?: (() => void) | undefined;
    derecha?: ReactNode;
}) {
    return (
        <header className={css.cabecera}>
            {alVolver !== undefined && (
                <button className={css.volver} onClick={alVolver} aria-label="Atrás">
                    ←
                </button>
            )}
            <div className={css.cabeceraTextos}>
                <h1 className={css.titulo}>{titulo}</h1>
                {subtitulo !== undefined && <span className={css.subtitulo}>{subtitulo}</span>}
            </div>
            {derecha !== undefined && <div style={{ marginLeft: 'auto' }}>{derecha}</div>}
        </header>
    );
}

export function Contenido({ children }: { children: ReactNode }) {
    return <main className={css.contenido}>{children}</main>;
}

export function BarraAccion({ children, nota }: { children: ReactNode; nota?: string | undefined }) {
    return (
        <>
            {nota !== undefined && <p className={css.barraNota}>{nota}</p>}
            <div className={css.barra}>{children}</div>
        </>
    );
}

export function Aviso({
    children,
    tono = 'warn',
    banda = false,
}: {
    children: ReactNode;
    tono?: 'warn' | 'info' | 'ok';
    banda?: boolean;
}) {
    const clases = [
        css.aviso,
        tono === 'info' ? css.avisoInfo : '',
        tono === 'ok' ? css.avisoOk : '',
        banda ? css.avisoBanda : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={clases} role={tono === 'warn' ? 'alert' : 'status'}>
            {children}
        </div>
    );
}

export function Tarjeta({
    children,
    tono,
    ...resto
}: {
    children: ReactNode;
    tono?: 'warn';
} & React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            className={`${css.tarjeta} ${tono === 'warn' ? css.tarjetaWarn : ''}`}
            {...resto}
        >
            {children}
        </div>
    );
}

export function Chip({
    children,
    tono,
}: {
    children: ReactNode;
    tono?: 'warn' | 'ok' | 'accent';
}) {
    const extra =
        tono === 'warn' ? css.chipWarn : tono === 'ok' ? css.chipOk : tono === 'accent' ? css.chipAccent : '';

    return <span className={`${css.chip} ${extra}`}>{children}</span>;
}
